import { useEffect, useState } from 'react';

interface ReasonModalProps {
  /** Dialog heading, e.g. "Reason for View / Print" or "Void Reason". */
  title: string;
  /** Appended to the title (e.g. the file number). */
  label?: string;
  placeholder?: string;
  submitLabel?: string;
  busyLabel?: string;
  busy?: boolean;
  /** When true (default) the submit button is disabled until a reason is entered. */
  requireReason?: boolean;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
}

/**
 * Small "enter a reason" dialog — used for View/Print and for Void (mirrors the
 * live app's reason modals). Lightweight controlled modal (no Bootstrap JS).
 */
export function ReasonModal({
  title,
  label,
  placeholder = 'Enter a reason…',
  submitLabel = 'Submit',
  busyLabel = 'Working…',
  busy = false,
  requireReason = true,
  onCancel,
  onSubmit,
}: ReasonModalProps) {
  const [reason, setReason] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const disabled = busy || (requireReason && reason.trim() === '');

  return (
    <div className="ast-modal-backdrop" onMouseDown={onCancel}>
      <div className="ast-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
        <div className="ast-modal-head">
          <h5 className="mb-0">{title}{label ? ` — ${label}` : ''}</h5>
          <button type="button" className="btn-close" aria-label="Close" onClick={onCancel} />
        </div>
        <div className="ast-modal-body">
          <textarea
            className="form-control"
            rows={3}
            autoFocus
            placeholder={placeholder}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <div className="ast-modal-foot">
          <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-ast-blue" disabled={disabled}
            onClick={() => onSubmit(reason.trim())}>
            {busy ? busyLabel : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
